// product-display — anti-corruption mapping between the marketplace catalog
// wire shape and the embed's display components.
//
// ⚠️ Corrected (Minor fix, fix wave 2026-09-15): this header used to say the
// catalog API returns money "with no flat price ... fields," which is now
// stale IN THIS FILE OF ALL PLACES — this module owns the `price.components[]`
// formatting (formatPriceHeadline, §4) that flat `price` object feeds. The
// catalog API (GET /api/v1/marketplace/catalog[/{slug}]) returns money as
// decimal dollar STRINGS ("10.00") both inside the legacy `charges[]` array
// AND inside the optional `price.components[]` object (R2 catalog-display
// redesign, design doc §5) — no `images`/`charge_model` flat fields, but
// `price` is very much a thing now. formatCurrency treats its input as
// dollars (TD-222 fix). These pure helpers parse string amounts to dollar
// numbers so the wire-to-display boundary is explicit and unit-tested
// (project rules §39, §44, §45) rather than scattered as `?.` guards.

import { formatCurrency } from "../../embed-shared/currency";
import type { CatalogPrice, PriceComponent, ProductCharge } from "../../embed-shared/types";

/**
 * Parse a decimal dollar string ("10.00") to a dollar number (10).
 * Returns null for null/undefined/empty/non-numeric input.
 */
export function parseDollarAmount(s: string | null | undefined): number | null {
  if (s == null || s === "") return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return n;
}

/** A charge mapped to display-ready dollar numbers. */
export interface DisplayCharge {
  id: string;
  label: string;
  oneTime: number | null;
  recurring: number | null;
  recurringInterval: string | null;
  // No `currency` member: TD-513 Task 4 (2026-09-19, Controller Ruling 13)
  // removed the dormant per-charge display currency — every formatter of
  // these values takes the marketplace-wide settings currency as a parameter
  // (StorefrontConfig context), so nothing ever consumed this member.
}

/** Map a wire ProductCharge to dollar-amount display values. */
export function chargeToDisplay(c: ProductCharge): DisplayCharge {
  return {
    id: c.id,
    label: c.charge_label,
    oneTime: parseDollarAmount(c.one_time_amount),
    recurring: parseDollarAmount(c.recurring_amount),
    recurringInterval: c.recurring_interval ?? null,
  };
}

/** Representative price shown on a compact catalog tile / PDP header. */
export interface DisplayPrice {
  amount: number;
  /** Set only when the displayed amount IS a recurring charge. */
  recurringLabel: string | null;
  /**
   * True when `amount` is the MINIMUM across multiple distinct one-time
   * charges (a variant/range) and should be prefixed with "From" (TD-308).
   * False for a single flat price.
   */
  isFromPrice: boolean;
}

/**
 * Derive a single representative price from a product's charges for compact
 * display.
 *
 * - When the product has MULTIPLE distinct one-time amounts, the representative
 *   price is the MINIMUM with `isFromPrice: true` (rendered "From $X.XX").
 * - Otherwise the first charge's one-time amount is used; if absent, the
 *   recurring amount is used (tagged with a recurring label).
 * - Returns null when the product has no charges.
 */
export function deriveDisplayPrice(
  charges: ProductCharge[] | null | undefined,
): DisplayPrice | null {
  if (!charges || charges.length === 0) return null;

  const display = charges.map(chargeToDisplay);

  // TD-308: variant/range detection — multiple distinct one-time amounts mean
  // the shown price is a minimum across options, so it is prefixed "From".
  const oneTimeAmounts = display
    .map((c) => c.oneTime)
    .filter((a): a is number => a != null);
  const distinctOneTime = new Set(oneTimeAmounts);
  if (distinctOneTime.size >= 2) {
    const min = Math.min(...oneTimeAmounts);
    return { amount: min, recurringLabel: null, isFromPrice: true };
  }

  const c = display[0];
  if (c.oneTime != null) {
    return { amount: c.oneTime, recurringLabel: null, isFromPrice: false };
  }
  if (c.recurring != null) {
    return {
      amount: c.recurring,
      recurringLabel: c.recurringInterval ? `/${c.recurringInterval}` : "recurring",
      isFromPrice: false,
    };
  }
  return { amount: 0, recurringLabel: null, isFromPrice: false };
}

// ────────────────────────────────────────────────────────────────────────────
// Product-price formatting (TD-308 / GH #45)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Format a FLAT product price.
 *
 * Per TD-308 display rules:
 *   - $0  → "$0.00"
 *   - >$0 → "$X.XX"
 *
 * `formatCurrency` already enforces 2 fraction digits via Intl.NumberFormat,
 * so both cases are handled; this helper is the explicit product-price entry
 * point so call sites read intent and future rules live in one place.
 */
export function formatProductPrice(amount: number, currency: string = "USD"): string {
  return formatCurrency(amount, currency);
}

/**
 * Format a "From"-style range/min product price: "From $X.XX".
 *
 * Used when a product has multiple variants and the shown amount is the
 * minimum of a range (e.g. "From $0.00" for a free-tier minimum). The amount
 * is itself formatted via the same rules as `formatProductPrice`.
 */
export function formatProductPriceFrom(amount: number, currency: string = "USD"): string {
  return `From ${formatProductPrice(amount, currency)}`;
}

// ────────────────────────────────────────────────────────────────────────────
// Price-headline formatting (R2 catalog-display redesign, design doc §4)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Maps a stored recurring_interval slug to its customer-facing word.
 * Returns null (never throws) for anything unrecognized.
 *
 * Fix round 1, CONTROLLER RULING: this call happens during React render, and
 * the ONLY error boundary anywhere under embed/marketplace/ is the whole-
 * mount `ErrorBoundary` in `embed/marketplace/index.tsx:28`, which wraps
 * `<MarketplaceEmbed>` in its entirety — there is no nested boundary per
 * product, per tile, or per PDP. A throw here would replace the customer's
 * ENTIRE storefront (catalog, cart, account, login) with the boundary's
 * generic "Something went wrong" message over ONE product's bad data, and
 * refreshing cannot help because the trigger is data, not a transient
 * fault. This repo's own convention is the opposite of a throw:
 * `CartLineItem.tsx:93` — "renders the neutral label instead of crashing
 * the embed into its ErrorBoundary." §4's "fail visibly" is satisfied
 * instead by excluding the anomalous component from headline selection and
 * `console.error`-ing it (see resolveComponents) — the raw slug never
 * reaches the screen, and the anomaly is loud in the console/telemetry
 * without being loud on the customer's screen.
 *
 * Reachability today is closed by the `ck_marketplace_products__
 * recurring_interval` CHECK constraint (migration
 * 134_product_price_cells.up.sql), which ships in the SAME migration as the
 * price cells and admits `'monthly'`/`'quarterly'`/`'yearly'`/`NULL` (quarterly via migration 179) — this path is
 * RARE in practice. But the wire value arrives here through an unvalidated
 * `PriceComponent.recurring_interval` union (a plain TS type gives zero
 * runtime protection), so the client must not assume that DB constraint is
 * the only gate between a bad value and this function.
 */
function intervalWord(interval: string): "month" | "quarter" | "year" | null {
  if (interval === "monthly") return "month";
  if (interval === "quarterly") return "quarter";
  if (interval === "yearly") return "year";
  return null;
}

/**
 * Resolves one PriceComponent to its bare (no "From ") amount + suffix
 * string, or null when it cannot be safely rendered (an unrecognized
 * recurring_interval) — the suffix belongs to the component ALONE (§4),
 * never to the product, and never assembled from a different component's
 * unit/interval.
 *
 * ⚠️ AMENDED (ITEM A fix, fix wave 2026-09-15, operator ruling 2026-09-15):
 * `currency` is now a required parameter — the caller's marketplace-wide
 * value (`useStorefrontConfig().currency`), NEVER read off the component
 * itself. ORIGINAL: both call sites below called `formatProductPrice(amount)`
 * with no currency argument, so `formatCurrency`'s `"USD"` default applied
 * BY ACCIDENT rather than by decision — correct only because every product
 * happened to be USD-priced so far. `PriceComponent` deliberately carries no
 * `currency` field (see the rejected-fix pinning test in
 * product-display.test.ts) — currency is marketplace-wide
 * (marketplace_settings.currency), never per-product.
 *
 * EXPORTED (fix wave 2026-09-15, round 2) for ChargeModelCard, whose job is
 * RENDERING one row per component — a different question from
 * `formatPriceHeadline`'s SELECTION of one headline. Routing the breakdown
 * through the headline formatter made `pickHeadline`'s device-basis one-time
 * exclusion (ITEM B) silently drop those rows from the PDP.
 */
export function formatComponent(component: PriceComponent, currency: string): string | null {
  const amount = parseDollarAmount(component.amount) ?? 0;
  if (component.charge_type === "one_time") {
    const suffix = component.basis === "device" ? `/${component.unit_label ?? "device"}` : "";
    return `${formatProductPrice(amount, currency)}${suffix}`;
  }
  // recurring
  const word = intervalWord(component.recurring_interval ?? "");
  if (word == null) return null;
  const suffix = component.basis === "device" ? ` per ${word}/${component.unit_label ?? "device"}` : `/${word}`;
  return `${formatProductPrice(amount, currency)}${suffix}`;
}

/**
 * The CART-side subscription formatter (device-count unit, Task 10, ruling
 * 4: "subscription lines show their pricing breakdown"). The cart wire
 * carries the line's resolved WHOLE-LINE per-period amount (Task 7's
 * `recurring_amount` — for a device line it is ALREADY folded by quantity;
 * for any other line the per-unit amount × quantity, ruling 8) plus its
 * `recurring_interval` — not the catalog's per-component shape. This helper
 * reuses the SAME interval vocabulary `intervalWord` maps for components
 * ("monthly" → "/month") so the cart's wording and the catalog's stay
 * byte-identical, rather than a second invented suffix.
 *
 * Fail-closed exactly like formatComponent: null for a null/absent amount
 * or an unrecognized interval — the caller omits the row rather than
 * printing a raw interval slug into customer copy.
 */
export function formatLineRecurring(
  amount: number | null | undefined,
  interval: string | null | undefined,
  currency: string,
): string | null {
  if (amount == null) return null;
  const word = intervalWord(interval ?? "");
  if (word == null) return null;
  return `${formatProductPrice(amount, currency)}/${word}`;
}

interface ResolvedComponent {
  component: PriceComponent;
  text: string;
}

/**
 * Resolves every component that CAN be safely rendered, excluding (and
 * `console.error`-ing) any that cannot — see intervalWord's doc comment for
 * why this degrades the ONE anomalous component rather than throwing.
 */
function resolveComponents(components: PriceComponent[], currency: string): ResolvedComponent[] {
  const resolved: ResolvedComponent[] = [];
  for (const component of components) {
    const text = formatComponent(component, currency);
    if (text == null) {
      // eslint-disable-next-line no-console -- rule 42: deliberate production-error-path observability.
      console.error(
        `formatPriceHeadline: excluding a component with an unrecognized recurring_interval "${component.recurring_interval}" from headline selection`,
        component,
      );
      continue;
    }
    resolved.push({ component, text });
  }
  return resolved;
}

/**
 * Picks the headline component per §4 from the already-RESOLVED (safely
 * renderable) set: the MINIMUM recurring component if any resolved, else
 * the MINIMUM one-time component. Returns null when neither pool has an
 * entry (every component was excluded, or the input was empty).
 *
 * ⚠️ AMENDED (device-count unit, Task 10, 2026-09-17) — the ONE-TIME pool's
 * ITEM B device-basis EXCLUSION is RETIRED with this unit. ORIGINAL (fix
 * wave 2026-09-15): the pool was `one_time && basis !== "device"`, because
 * no production caller supplied a `device_count`, so a device-basis
 * one-time term contributed $0 to every real bill and advertising it (it
 * usually won the MINIMUM comparison against a store term) stated a rate
 * the customer would never be charged. CURRENT: operator ruling 1 (quantity
 * IS the device count) landed in Tasks 3/3.5/3.75 — a device-basis
 * one-time component bills for real at quantity × rate, so the $0 premise
 * is dead. With the exclusion left in, a device-only one-time product
 * (e.g. "$50.00/device") would have an EMPTY pool and no headline at all —
 * silently priceless, the exact harm this retirement exists to close — and
 * a mixed {store, device} one-time product would understate its entry
 * price. The pool is every resolved one_time component, basis-blind, as it
 * was before the ITEM B fix. The device rate legitimately wins the MINIMUM
 * comparison when it is the smaller number, and its "/device" suffix (via
 * its own component's text) keeps the headline honest about the basis.
 *
 * The RECURRING pool is deliberately NOT filtered by basis (see the retired
 * 2026-09-16/09-17 amendment chain in the git history for why a basis
 * filter here was rejected: the fix for an unbuyable product was the
 * product-level purchasability gate, which this unit also retires — the
 * server guards it mirrored are gone, Tasks 3/5).
 */
function pickHeadline(resolved: ResolvedComponent[]): ResolvedComponent | null {
  const recurring = resolved.filter((r) => r.component.charge_type === "recurring");
  const pool =
    recurring.length > 0
      ? recurring
      : resolved.filter((r) => r.component.charge_type === "one_time");
  if (pool.length === 0) return null;
  return pool.reduce((min, r) =>
    (parseDollarAmount(r.component.amount) ?? 0) < (parseDollarAmount(min.component.amount) ?? 0) ? r : min,
  );
}

// ⚠️ REMOVED (device-count unit, Task 10, 2026-09-17) — the two TEMPORARY
// display predicates this module carried since the 2026-09-15/16 fix waves
// are deleted, per their own DELETE notes, now that the device-count unit
// landed their premises' deaths:
//
//   - `isCurrentlyBillable(component)` (fix wave 2026-09-15, ITEM C) —
//     suppressed a DEVICE-basis ONE-TIME component from every presentation
//     surface (ChargeModelCard's row filter, ProductDetailView's
//     showBreakdown count, formatPriceHeadline's "From " prefix count)
//     because it billed $0: no production caller supplied a device_count.
//     Tasks 3/3.5/3.75 made quantity the device count (operator ruling 1),
//     so the component bills quantity × rate for real and MUST be
//     displayed. Leaving the predicate in would keep device-priced products
//     silently priceless — the exact harm this retirement closes.
//
//   - `isCurrentlyPurchasable(components)` (fix wave 2026-09-16, verified
//     gate finding 2) — the product-level gate that refused a headline for
//     any product the server's cart-add would 422 (mirroring the
//     `ErrChargeRecurringOnly` and `ErrDeviceCountRequired` guards). Task 5
//     LIFTED the ErrChargeRecurringOnly cell-arm guard (recurring
//     cell-priced products are sellable again — TD-494's core), and Task 3
//     narrowed ErrDeviceCountRequired to a genuine validation (a positive
//     count is derived from every well-formed add). A mirror of guards that
//     no longer fire would suppress real prices for sellable products.
//
// The suppression tests that pinned these behaviors are deleted alongside,
// each preserving its original intent in an ORIGINAL (superseded) comment
// (rule 104). Sweep: `command grep -rn "isCurrentlyBillable\|
// isCurrentlyPurchasable" _src/apps/web/src` returns only this removal
// record.

/**
 * Legacy (`charges[]`-only, TD-308) headline string — used when `price` is
 * absent.
 *
 * ⚠️ AMENDED (ITEM A fix, fix wave 2026-09-15, operator ruling 2026-09-15):
 * formats using the caller's marketplace-wide `currency`, NOT
 * `legacy.currency` (deriveDisplayPrice's per-CHARGE currency, sourced from
 * the legacy `charges[].currency` wire field). ORIGINAL: `legacy.currency`
 * — a genuinely per-product value on the wire — was treated as the display
 * authority. CURRENT: the ruling retires per-product currency as an
 * authority entirely; `deriveDisplayPrice`/`chargeToDisplay` are UNCHANGED
 * (their `.currency` field still exists and is still unit-tested) but this
 * formatting site no longer reads it.
 * ⚠️ AMENDED AGAIN (TD-513 Task 4, 2026-09-19, Controller Ruling 13): the
 * prior paragraph's parenthetical is superseded — the `DisplayCharge.currency`
 * and `DisplayPrice.currency` members (and chargeToDisplay's `|| "USD"`
 * fallback) are REMOVED; re-derived first that no formatter anywhere
 * consumed them (every caller passes the StorefrontConfig currency). The
 * dormant non-USD hazard the fallback represented is now unrepresentable.
 */
function legacyHeadline(charges: ProductCharge[] | null | undefined, currency: string): string | null {
  const legacy = deriveDisplayPrice(charges);
  if (!legacy) return null;
  const priceLabel = legacy.isFromPrice
    ? formatProductPriceFrom(legacy.amount, currency)
    : formatProductPrice(legacy.amount, currency);
  if (!legacy.recurringLabel) return priceLabel;
  // recurringLabel is either "/<interval>" (slash-prefixed, no gap needed)
  // or the bare word "recurring" (list endpoint omits the interval) — see
  // deriveDisplayPrice above. NOTE this legacy `charges[]` path does NOT
  // word-map the interval the way intervalWord does for price.components[]
  // — it passes the DB's raw `monthly`/`quarterly`/`yearly` value straight through
  // (e.g. "$9.99/monthly", not "$9.99/month"). That `/month` vs `/monthly`
  // wording split between the two paths is real and pre-existing; fixing it
  // is explicitly deferred to a consolidated follow-up (fix round 1 scope
  // note), not addressed here.
  return legacy.recurringLabel.startsWith("/") ? `${priceLabel}${legacy.recurringLabel}` : `${priceLabel} ${legacy.recurringLabel}`;
}

/**
 * Builds the complete customer-facing headline price string (design doc §4).
 *
 * The string is a **prefix** (`From ` or nothing) and a **suffix**
 * determined by the headline component ALONE — they are independent and
 * must never be conflated: the prefix depends only on how many components
 * RESOLVED (resolveComponents) for this customer (⚠️ AMENDED, device-count
 * unit Task 10: ORIGINAL counted only PRESENTABLE components via the now-
 * retired `isCurrentlyBillable`); the
 * suffix depends only on which single component is the headline (never
 * assembled from a different component's basis/unit/interval — "the
 * minimum wins and the string carries THAT component's basis, unit and
 * interval").
 *
 * Reads `price.components[]` (Task 4's wire contract) when present and
 * non-empty.
 *
 * ⚠️ AMENDED (I2 fix, fix wave 2026-09-15) — an ABSENT `price` and a
 * present-but-EMPTY one are different wire signals (types.ts's
 * `CatalogPrice` doc). ORIGINAL: "`price` is OMITTED by the server (never
 * an empty-but-present object) for a product with no price cells or a
 * fail-closed anomaly, so an absent/empty `price` here is the normal
 * 'use charges[]' signal, not an error." CURRENT: only an ABSENT `price`
 * falls back to the legacy `charges[]` path (TD-308, `deriveDisplayPrice`);
 * a PRESENT `price` with `components: []` means "priced but unresolvable
 * right now" and returns null ("Contact for pricing"), because `charges[]`
 * can be a stale pre-cell-grid amount for a product meant to be cell-priced.
 *
 * Returns null when NEITHER source yields a price, including when every
 * component in a non-empty `price.components[]` had an unrecognized
 * interval (see intervalWord) — this function NEVER throws and NEVER emits
 * a raw interval slug into customer copy; an unresolvable component is
 * excluded and logged instead (Ruling, fix round 1).
 *
 * ⚠️ AMENDED (fix wave 2026-09-16, verified gate finding 2) — a THIRD null
 * cause. ORIGINAL: a non-empty `price.components[]` always produced a
 * headline unless every component failed to resolve. CURRENT: it also
 * returned null when `isCurrentlyPurchasable(components)` was false — the
 * product could not be added to a cart, so quoting any rate for it
 * advertised a price the customer could never be charged. All null causes
 * landed on the same "Contact for pricing" treatment at the call sites.
 * ⚠️ AMENDED AGAIN (device-count unit, Task 10, 2026-09-17) — that third
 * null cause is RETIRED with `isCurrentlyPurchasable` itself (see the
 * REMOVED record below pickHeadline): the server guards it mirrored are
 * lifted/narrowed (Tasks 3/5), so recurring and device-priced products are
 * sellable and their headlines are back.
 *
 * ⚠️ AMENDED (ITEM A fix, fix wave 2026-09-15, operator ruling 2026-09-15):
 * `currency` is a new parameter, defaulting to `"USD"` to match
 * `marketplace_settings.currency`'s own DB default and keep every existing
 * call site (including this module's own tests) compiling unchanged.
 * PRODUCTION callers must NOT rely on the default — they pass the live
 * `useStorefrontConfig().currency` explicitly, so the displayed currency
 * tracks the operator's marketplace-wide setting rather than accidentally
 * defaulting. See ITEM B's amendment on `pickHeadline` for the separate
 * fix-wave change to which component wins headline selection.
 */
export function formatPriceHeadline(
  price: CatalogPrice | null | undefined,
  charges: ProductCharge[] | null | undefined,
  currency: string = "USD",
): string | null {
  if (price == null) {
    return legacyHeadline(charges, currency);
  }
  const components = price.components;
  if (!components || components.length === 0) {
    return null;
  }

  // ⚠️ RESOLVE FIRST — `resolveComponents` is the only place an
  // unrecognized `recurring_interval` is console.error'd (fix round 1's
  // "fail closed per-component, never throw" ruling), and that anomaly is an
  // operator DATA-INTEGRITY signal, not a presentation detail (project rule
  // #42: deliberate production-error-path observability). Resolving before
  // any selection keeps the signal firing for every component.
  const resolved = resolveComponents(components, currency);

  // ⚠️ REMOVED (device-count unit, Task 10): the product-level
  // `isCurrentlyPurchasable` gate that stood here (fix wave 2026-09-16) is
  // retired — the server guards it mirrored are gone (Task 5 lifted
  // ErrChargeRecurringOnly on the cell arm; Task 3 narrowed
  // ErrDeviceCountRequired to a real validation satisfied by every
  // well-formed add). See the REMOVED record below pickHeadline.
  const headline = pickHeadline(resolved);
  if (!headline) return null;

  // The prefix counts components that RESOLVED, not the raw wire array — a
  // product with 2 components where 1 had a bad interval has exactly ONE
  // renderable option, and "From " would misleadingly imply a range.
  // ⚠️ AMENDED (device-count unit, Task 10): ORIGINAL (fix waves
  // 2026-09-15 ITEM C / 2026-09-16) counted only components passing
  // `isCurrentlyBillable` — device-basis one-time terms were excluded so
  // the prefix never implied a range whose second price nothing rendered.
  // CURRENT: that predicate is retired (it billed $0; it now bills for
  // real), so the count is every RESOLVED component again, as it was
  // before the fix waves.
  return resolved.length > 1 ? `From ${headline.text}` : headline.text;
}
